import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Card3D, CardHoverPreview, cardImageStyle } from "@/components/Card3D";
import { SelectableCardRow } from "@/components/CardRows";
import { ArenaProfile } from "@/components/ArenaBits";
import { LoadoutManager } from "@/components/LoadoutManager";
import { EnhanceTicket } from "@/components/EnhanceTicket";
import { HouseBadge } from "@/components/HouseBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Copy, Layers, Lock, RotateCw, Shield, User, Tag, Coins, Ban, Store, CalendarDays, Wand2, Sparkles, TrendingUp, ChevronsUp, Swords, Skull, Drama, Flag, FlaskConical, Wrench } from "lucide-react";
import { useState } from "react";
import { copyText } from "@/lib/clipboard";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ReportButton } from "@/components/ReportModal";

const RARITY_ORDER = ["common", "uncommon", "rare", "epic", "legendary", "goddess"];

function LevelPanel({ card, isOwner, onEvolve, evolving }) {
  const level = card.level ?? 1;
  const evo = card.evolution ?? 0;
  const maxLevel = 10;
  const atMax = level >= maxLevel;
  const nextRarity = RARITY_ORDER[RARITY_ORDER.indexOf(card.rarity) + 1];
  const isMaxRarity = !nextRarity;
  const xpPct = atMax ? 100 : Math.min(100, Math.round(((card.xp ?? 0) / Math.max(1, card.xp_to_next ?? 1)) * 100));
  return (
    <div className="mt-4 hud-pill rounded-xl p-4" data-testid="level-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-amber-300/70 flex items-center gap-1.5">
          <TrendingUp className="h-3.5 w-3.5" /> Card Progression
        </div>
        {evo > 0 && (
          <span data-testid="evolution-badge" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-fuchsia-300 bg-fuchsia-500/15 border border-fuchsia-400/30 px-2 py-0.5 rounded-md">
            <Sparkles className="h-3 w-3" /> Evolution {evo}
          </span>
        )}
      </div>

      <div className="flex items-end justify-between">
        <div className="font-display text-3xl text-white tracking-[0.04em]" data-testid="level-value">
          LV {level}<span className="text-white/30 text-lg"> / {maxLevel}</span>
        </div>
        {level > 1 && (
          <div className="font-mono text-xs text-emerald-300" data-testid="level-bonus">+{card.level_bonus_pct ?? (level - 1) * 5}% all stats</div>
        )}
      </div>

      <div className="mt-2 h-2 w-full rounded-full bg-white/10 overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-emerald-400 transition-all duration-500" style={{ width: `${xpPct}%`, boxShadow: "0 0 10px rgba(251,191,36,0.5)" }} />
      </div>
      <div className="mt-1 font-mono text-[10px] text-white/40">
        {atMax ? (isMaxRarity ? "Peak form — the apex rarity." : "MAX LEVEL — ready to evolve.") : `${card.xp ?? 0} / ${card.xp_to_next} XP to Lv ${level + 1}`}
      </div>

      <p className="mt-2 text-[11px] text-white/45 leading-snug">
        Cards earn their own XP only by playing — Arena, Race, Cavern & Talent Show. XP can never be bought.
      </p>

      {isOwner && card.can_evolve && (
        <Button data-testid="evolve-button" disabled={evolving} onClick={onEvolve}
          className="w-full mt-3 bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white hover:opacity-90 font-medium">
          <ChevronsUp className="h-4 w-4 mr-1.5" /> {evolving ? "Evolving…" : `Evolve → ${nextRarity?.toUpperCase()}`}
        </Button>
      )}
      {isOwner && card.can_evolve && (
        <p className="mt-1.5 text-[10px] text-white/40 text-center">Promotes this edition to {nextRarity}, re-rolls its stats higher, and resets to Lv 1. Permanent.</p>
      )}
    </div>
  );
}

function EditionSelector({ card }) {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ["template-editions", card.template_id],
    queryFn: async () => (await api.get(`/templates/${card.template_id}/editions`)).data,
    enabled: !!card.template_id,
  });
  const editions = data?.editions || [];
  if (editions.length < 2) return null;
  return (
    <div className="mt-4" data-testid="edition-selector">
      <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40 mb-1.5">View another edition</div>
      <Select value={card.id} onValueChange={(val) => { if (val !== card.id) navigate(`/card/${val}`); }}>
        <SelectTrigger data-testid="edition-select-trigger" className="w-full sm:w-72 bg-[hsl(220_18%_12%)] border-white/10 text-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[hsl(222_22%_8%)] border-white/10 text-white max-h-72">
          {editions.map((e) => (
            <SelectItem key={e.id} value={e.id} data-testid={`edition-option-${e.edition_number}`}>
              Edition #{e.edition_number} · {e.serial}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function CardDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const qc = useQueryClient();
  const [sellOpen, setSellOpen] = useState(false);
  const [enhanceOpen, setEnhanceOpen] = useState(false);

  const { data: card, isLoading } = useQuery({
    queryKey: ["card", id],
    queryFn: async () => (await api.get(`/cards/${id}`)).data,
  });

  const isOwnerEarly = user && card && card.owner_id === user.id;
  const { data: enhanceReq } = useQuery({
    queryKey: ["enhance-req", id],
    queryFn: async () => (await api.get(`/cards/${id}/enhance-request`)).data,
    enabled: !!isOwnerEarly,
  });

  const cancelListing = useMutation({
    mutationFn: async () => (await api.post(`/market/${card.market_listing.id}/cancel`)).data,
    onSuccess: () => { toast.success("Listing cancelled — card returned to your Playden"); qc.invalidateQueries({ queryKey: ["card", id] }); qc.invalidateQueries({ queryKey: ["market"] }); },
    onError: (e) => toast.error(apiError(e, "Failed")),
  });
  const quickSell = useMutation({
    mutationFn: async () => (await api.post(`/cards/${id}/sellback`)).data,
    onSuccess: (d) => { toast.success(`Sold to the store for ${d.credited} credits`); qc.invalidateQueries({ queryKey: ["my-cards"] }); navigate("/collection"); },
    onError: (e) => toast.error(apiError(e, "Sell failed")),
  });
  const evolve = useMutation({
    mutationFn: async () => (await api.post(`/cards/${id}/evolve`)).data,
    onSuccess: (d) => { toast.success(`✨ Evolved to ${d.rarity}!${d.minted_trait?.name ? ` Minted a new trait: ${d.minted_trait.name}.` : ""} A new grind begins.`); qc.invalidateQueries({ queryKey: ["card", id] }); qc.invalidateQueries({ queryKey: ["my-cards"] }); },
    onError: (e) => toast.error(apiError(e, "Evolve failed")),
  });
  const refreshCard = () => { qc.invalidateQueries({ queryKey: ["card", id] }); qc.invalidateQueries({ queryKey: ["adv-state-equip"] }); };
  const equip = useMutation({
    mutationFn: async (item_id) => (await api.post("/adventure/equip", { card_id: id, item_id })).data,
    onSuccess: () => { toast.success("Equipped"); refreshCard(); },
    onError: (e) => toast.error(apiError(e, "Couldn't equip")),
  });
  const unequip = useMutation({
    mutationFn: async (item_id) => (await api.post("/adventure/unequip", { card_id: id, item_id })).data,
    onSuccess: () => { toast.success("Unequipped — returned to your bag"); refreshCard(); },
    onError: (e) => toast.error(apiError(e, "Couldn't unequip")),
  });

  if (isLoading || !card) {
    return <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 text-center text-white/40">Loading…</div>;
  }

  const isOwner = Boolean(user && card.owner_id === user.id);
  const canEditGear = isOwner && !card.locked_out && !card.locked_in_trade && !card.locked_in_market && !card.locked;
  const copySerial = async () => {
    const ok = await copyText(card.serial);
    ok ? toast.success("Serial copied") : toast.error("Couldn't copy — copy it manually");
  };

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-10 overflow-x-hidden">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-white/60 mb-6" data-testid="card-back-button">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back
      </Button>
      <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-start">
        <div className="flex justify-center lg:justify-start">
          <Card3D card={card} size="xl" />
        </div>
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-300/80">CARD</div>
          <h1 className="font-display text-2xl sm:text-4xl md:text-5xl text-white tracking-[0.04em] mt-1 break-words leading-[1.05]">
            {card.locked ? "??? LOCKED ???" : card.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <div className={`rarity-${card.rarity} font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-1 rounded-md`} style={{ color: "var(--label)", background: "rgba(0,0,0,0.45)", border: "1px solid var(--edge)" }}>
              {card.rarity}
            </div>
            {card.edition_number && (
              <div className="hud-pill font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-1 rounded-md text-white/70">
                Edition #{card.edition_number}
              </div>
            )}
            {(card.edition_date || card.created_at) && (
              <div className="hud-pill font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-1 rounded-md text-white/70 flex items-center gap-1.5" data-testid="card-date-chip" title={card.edition_date ? "Edition date (set by the vault)" : "Mint date"}>
                <CalendarDays className="h-3 w-3 text-cyan-300" />
                {card.edition_date || new Date(card.created_at).toLocaleDateString()}
              </div>
            )}
          </div>

          {card.distribution_mode === "shared_claim" && <div data-testid="card-digital-series" className="mt-3 text-xs font-mono text-emerald-300">DIGITAL ONLY · OPEN EDITION{card.edition_count != null ? ` · ${card.edition_count} minted` : ""}</div>}
          {card.locked_out && <div role="status" data-testid="card-permanent-lockout" className="mt-4 border-y border-red-400/30 py-4 text-sm text-red-200"><strong className="flex items-center gap-2"><Lock className="h-4 w-4" /> Permanently locked out</strong><p className="mt-2">This card remains in your Playden with its art, stats and equipment intact. It cannot be played, traded, sold, upgraded or recovered.</p></div>}

          {isOwner && (
            <div className="mt-3" data-testid="card-house-panel">
              <HouseBadge card={card} />
            </div>
          )}

          {card.arena && (
            <div className="mt-4 hud-pill rounded-xl p-3" data-testid="arena-stats-panel">
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40 mb-2">Arena Combat Profile</div>
              <ArenaProfile arena={card.arena} card={card} interactive={isOwner && !card.in_use} />
            </div>
          )}

          <div className="mt-4 hud-pill rounded-xl p-3" data-testid="card-adventure-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="font-display text-base uppercase tracking-wider text-white inline-flex items-center gap-2"><Wrench className="h-4 w-4 text-cyan-300" /> Loadout</div>
                {card.profession_name && <span data-testid="card-profession" className="inline-flex items-center gap-1 rounded-full bg-amber-400/10 border border-amber-400/30 px-2 py-0.5 font-mono text-[10px] text-amber-200">{card.profession_name}</span>}
              </div>
              <LoadoutManager card={card} canEdit={canEditGear} equipping={equip.isPending}
                onEquip={(itemId) => equip.mutate(itemId)} onUnequip={(itemId) => unequip.mutate(itemId)} />
              {!canEditGear && (card.gear || []).length === 0 && (card.loadout || []).length === 0 && (
                <div className="text-white/35 text-[11px] mt-2">No gear equipped. Forge &amp; equip some in <span className="text-amber-300">Adventure</span>.</div>
              )}
            </div>

          {!card.locked && card.level != null && (
            <LevelPanel card={card} isOwner={isOwner && !card.locked_out} onEvolve={() => evolve.mutate()} evolving={evolve.isPending} />
          )}

          {card.template_id && <EditionSelector card={card} />}

          {!card.locked && card.description && (
            <p className="text-white/65 mt-4 leading-relaxed text-sm">{card.description}</p>
          )}
          {!card.locked && card.lore && (
            <div className="mt-4 hud-pill rounded-xl p-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300/70 mb-1">Lore</div>
              <p className="text-white/65 text-sm leading-relaxed italic">{card.lore}</p>
            </div>
          )}

          <div className="mt-6 grid grid-cols-2 gap-3">
            <div className="hud-pill rounded-xl p-3">
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40">Serial</div>
              <div className="font-mono text-sm text-white mt-1 break-all">{card.serial}</div>
              <button onClick={copySerial} className="text-cyan-300 text-xs mt-2 inline-flex items-center gap-1" data-testid="copy-serial-button">
                <Copy className="h-3 w-3" /> Copy
              </button>
            </div>
            <div className="hud-pill rounded-xl p-3">
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40">Owner</div>
              <div className="text-white mt-1 text-sm flex items-center gap-2">
                <User className="h-3.5 w-3.5" />
                {card.owner_name || "Unknown"}
              </div>
              {isOwner && <div className="text-emerald-300 text-xs mt-1 inline-flex items-center gap-1"><Shield className="h-3 w-3" /> You own this</div>}
            </div>
          </div>

          {card.locked && (
            <div className="mt-6 hud-pill rounded-xl p-4 flex items-center gap-3">
              <Lock className="h-5 w-5 text-white/60" />
              <div className="text-sm text-white/65">This card's art is encrypted to its owner. Trade or redeem to unlock.</div>
            </div>
          )}

          {isOwner && !card.locked_out && (
            <div className="mt-6 hud-pill rounded-xl p-4" data-testid="sell-panel">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300/70 mb-2">Sell this card</div>
              {card.locked_in_trade ? (
                <div className="text-sm text-amber-300/90 flex items-center gap-2"><Lock className="h-4 w-4" /> Locked in a trade — resolve it first.</div>
              ) : card.locked_in_market && card.market_listing ? (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-sm text-white/80 flex items-center gap-2">
                    <Tag className="h-4 w-4 text-cyan-300" />
                    Listed for <span className="text-amber-300 font-mono">{card.market_listing.price.toLocaleString()}</span> credits
                    <span className="text-white/40 inline-flex items-center gap-1"><Lock className="h-3 w-3" /> void-locked</span>
                  </div>
                  <Button variant="outline" data-testid="cancel-listing-button" disabled={cancelListing.isPending} onClick={() => cancelListing.mutate()} className="border-white/15 text-white"><Ban className="h-4 w-4 mr-1" /> Cancel Listing</Button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <Button data-testid="list-market-button" onClick={() => setSellOpen(true)} className="bg-cyan-400 text-black hover:bg-cyan-300"><Tag className="h-4 w-4 mr-1" /> List on Market</Button>
                  <Button variant="outline" data-testid="quick-sell-button" disabled={quickSell.isPending} onClick={() => { if (window.confirm(`Quick-sell to the store for ${card.sellback_value} credits? This permanently removes the card from your Playden.`)) quickSell.mutate(); }} className="border-white/15 text-white"><Coins className="h-4 w-4 mr-1" /> Quick Sell ({card.sellback_value}c)</Button>
                  <span className="text-[11px] text-white/40">Quick sell returns 1/10 of the card's value ({card.base_value}c base).</span>
                </div>
              )}
            </div>
          )}

          {isOwner && !card.locked_out && (
            <div className="mt-6 hud-pill rounded-xl p-4" data-testid="enhance-panel">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fuchsia-300/80 mb-2 flex items-center gap-1.5"><Wand2 className="h-3.5 w-3.5" /> Visual enhancement</div>
              {enhanceReq && ["pending", "proposed"].includes(enhanceReq.status) ? (
                <EnhanceTicket requestId={enhanceReq.id} isAdmin={false}
                  onResolved={() => { qc.invalidateQueries({ queryKey: ["enhance-req", id] }); qc.invalidateQueries({ queryKey: ["card", id] }); refresh(); }} />
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <Button data-testid="request-enhance-button" onClick={() => setEnhanceOpen(true)} className="bg-fuchsia-500 text-white hover:bg-fuchsia-400">
                    <Wand2 className="h-4 w-4 mr-1" /> Request Enhancement
                  </Button>
                  <span className="inline-flex items-center gap-1 font-mono text-sm text-amber-300" data-testid="enhance-cost">
                    <Coins className="h-4 w-4" /> {(card.enhance_cost ?? 250).toLocaleString()}
                  </span>
                  <span className="text-[11px] text-white/40">Open a ticket: chat with the vault and pick from multiple HQ versions before applying one.</span>
                  {enhanceReq && enhanceReq.status === "fulfilled" && <span className="text-[11px] text-emerald-300">✓ Last request applied</span>}
                </div>
              )}
            </div>
          )}

          {isOwner && !card.locked_out && !card.locked_in_trade && !card.locked_in_market && (
            <div className="mt-6 hud-pill rounded-xl p-4" data-testid="use-card-panel">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300/80 mb-3 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Put this card to work</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  { to: "/arena", label: "Arena", icon: Swords, color: "#fb7185", testid: "use-arena" },
                  { to: "/cavern", label: "Cavern", icon: Skull, color: "#a78bfa", testid: "use-cavern" },
                  { to: "/talentshow", label: "Talent Show", icon: Drama, color: "#fb923c", testid: "use-talentshow" },
                  { to: "/race", label: "Gauntlet", icon: Flag, color: "#22d3ee", testid: "use-race" },
                  { to: "/upgrade", label: "Upgrade", icon: FlaskConical, color: "#c084fc", testid: "use-upgrade" },
                  { to: "/requests", label: "Trade", icon: RotateCw, color: "#34d399", testid: "use-trades" },
                ].map((d) => (
                  <Link key={d.to} to={`${d.to}?preselect=${card.id}`} data-testid={d.testid}>
                    <div className="rounded-lg border border-white/10 hover:border-white/25 bg-white/[0.03] hover:bg-white/[0.06] px-3 py-2.5 flex items-center gap-2 transition-colors group">
                      <d.icon className="h-4 w-4 shrink-0" style={{ color: d.color }} />
                      <span className="text-sm text-white/85 group-hover:text-white truncate">{d.label}</span>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="text-[11px] text-white/40 mt-2">Jump straight to where this card can battle, perform, race, or be improved.</div>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/collection">
              <Button variant="outline" className="border-white/15 text-white"><Layers className="h-4 w-4 mr-1" /> My Playden</Button>
            </Link>
            {user && !isOwner && card.locked_in_market && (
              <Link to="/store">
                <Button data-testid="view-in-store-button" className="bg-cyan-400 text-black hover:bg-cyan-300"><Store className="h-4 w-4 mr-1" /> Buy on Market</Button>
              </Link>
            )}
            {user && !isOwner && !card.locked_out && !card.locked_in_market && (
              <Link to={`/requests?want_card=${encodeURIComponent(card.id)}&want_title=${encodeURIComponent(card.title || "")}&want_owner=${encodeURIComponent(card.owner_id || "")}&want_owner_name=${encodeURIComponent(card.owner_name || "")}`}>
                <Button data-testid="propose-trade-button" className="bg-cyan-400 text-black hover:bg-cyan-300">
                  <RotateCw className="h-4 w-4 mr-1" /> Propose Direct Trade
                </Button>
              </Link>
            )}
            {user && !isOwner && (
              <ReportButton
                targetType="card"
                targetId={card.id}
                contextLabel={card.title ? `Card "${card.title}"` : `Card ${card.id.slice(0, 8)}`}
                className="ml-auto self-center px-2 py-1 rounded border border-white/10 hover:border-rose-300/50"
              />
            )}
          </div>
        </div>
      </div>

      {sellOpen && (
        <ListOnMarketDialog card={card} onClose={() => setSellOpen(false)} onListed={() => qc.invalidateQueries({ queryKey: ["card", id] })} />
      )}
      {enhanceOpen && (
        <EnhanceRequestDialog card={card} onClose={() => setEnhanceOpen(false)} onDone={() => { qc.invalidateQueries({ queryKey: ["enhance-req", id] }); refresh(); }} />
      )}
    </div>
  );
}

function EnhanceRequestDialog({ card, onClose, onDone }) {
  const { user } = useAuth();
  const [kind, setKind] = useState("image");
  const [prompt, setPrompt] = useState("");
  const [rf, setRf] = useState({ title: "", lore: "", description: "" });
  const { data: inv } = useQuery({ queryKey: ["inventory"], queryFn: async () => (await api.get("/inventory")).data });
  const tokenQty = (inv?.tokens || []).find((t) => t.type === "hq_enhancement")?.qty || 0;
  const [useToken, setUseToken] = useState(false);
  const free = useToken && tokenQty > 0;
  const cost = kind === "text" ? (card.enhance_text_cost ?? 150) : (card.enhance_cost ?? 250);
  const balance = user?.credits ?? 0;
  const affordable = free || balance >= cost;
  const textFilled = rf.title.trim() || rf.lore.trim() || rf.description.trim();
  const ready = kind === "text" ? !!textFilled : !!prompt.trim();
  const submit = useMutation({
    mutationFn: async () => {
      if (kind === "text") {
        const parts = [];
        if (rf.title.trim()) parts.push(`Name → ${rf.title.trim()}`);
        if (rf.lore.trim()) parts.push(`Lore → ${rf.lore.trim()}`);
        if (rf.description.trim()) parts.push(`Description → ${rf.description.trim()}`);
        return (await api.post(`/cards/${card.id}/enhance-request`, {
          prompt: parts.join("\n") || "Rewrite request",
          kind: "text", use_token: free,
          requested_title: rf.title.trim() || null,
          requested_lore: rf.lore.trim() || null,
          requested_description: rf.description.trim() || null,
        })).data;
      }
      return (await api.post(`/cards/${card.id}/enhance-request`, { prompt, kind: "image", use_token: free })).data;
    },
    onSuccess: () => { toast.success(free ? "Request sent — HQ Enhancement token used" : `Request sent — ${cost.toLocaleString()} credits charged`); onDone(); onClose(); },
    onError: (e) => toast.error(apiError(e, "Couldn't submit request")),
  });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white tracking-[0.08em]">REQUEST ENHANCEMENT</DialogTitle>
          <DialogDescription className="sr-only">Request a visual enhancement or a name and lore rewrite for this card edition.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* request type */}
          <div className="grid grid-cols-2 gap-2" data-testid="enhance-kind-picker">
            <button data-testid="enhance-kind-image" onClick={() => setKind("image")}
              className={`rounded-lg border px-3 py-2 text-left transition-colors ${kind === "image" ? "border-fuchsia-400 bg-fuchsia-500/15" : "border-white/10 hover:border-white/25"}`}>
              <div className="text-sm text-white font-medium">Enhance image</div>
              <div className="text-[10px] text-white/45">Sharper / redrawn art</div>
            </button>
            <button data-testid="enhance-kind-text" onClick={() => setKind("text")}
              className={`rounded-lg border px-3 py-2 text-left transition-colors ${kind === "text" ? "border-fuchsia-400 bg-fuchsia-500/15" : "border-white/10 hover:border-white/25"}`}>
              <div className="text-sm text-white font-medium">Rewrite name & lore</div>
              <div className="text-[10px] text-white/45">No new image · text only</div>
            </button>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-white/10 bg-black/30 px-3 py-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">Cost {kind === "text" ? "(text rewrite)" : `(${card.rarity})`}</span>
            <span className="inline-flex items-center gap-1 font-mono text-sm text-amber-300" data-testid="enhance-dialog-cost"><Coins className="h-4 w-4" /> {cost.toLocaleString()}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-white/40">Your balance</span>
            <span className={affordable ? "text-white/70" : "text-red-400"}>{balance.toLocaleString()} credits</span>
          </div>

          {tokenQty > 0 && (
            <button type="button" data-testid="enhance-use-token" onClick={() => setUseToken((v) => !v)}
              className={`w-full rounded-lg border px-3 py-2 flex items-center gap-2.5 text-left transition-colors ${free ? "border-cyan-400 bg-cyan-500/15" : "border-white/10 hover:border-cyan-400/40"}`}>
              <Sparkles className={`h-4 w-4 shrink-0 ${free ? "text-cyan-300" : "text-white/40"}`} />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white font-medium">Use an HQ Enhancement token — make this free</div>
                <div className="text-[10px] text-white/45">You have {tokenQty} · waives the {cost.toLocaleString()} credit cost</div>
              </div>
              <div className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${free ? "bg-cyan-400 border-cyan-400" : "border-white/30"}`}>{free && <span className="text-black text-[10px] font-bold">✓</span>}</div>
            </button>
          )}

          {kind === "text" ? (
            <div className="space-y-3">
              <p className="text-[12px] text-white/55">Draft the new <span className="text-white">name, lore and/or description</span> for <span className="text-white">{card.title}</span>. Fill in any you want changed — only this edition is affected after you accept.</p>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">New name</Label>
                <Input data-testid="enhance-req-title" value={rf.title} onChange={(e) => setRf((f) => ({ ...f, title: e.target.value }))} maxLength={120}
                  placeholder={card.title} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">New lore</Label>
                <Textarea data-testid="enhance-req-lore" value={rf.lore} onChange={(e) => setRf((f) => ({ ...f, lore: e.target.value }))} maxLength={4000} rows={3}
                  placeholder="The backstory / flavor text you'd like…" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">New description</Label>
                <Textarea data-testid="enhance-req-description" value={rf.description} onChange={(e) => setRf((f) => ({ ...f, description: e.target.value }))} maxLength={2000} rows={2}
                  placeholder="A short tagline / description…" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-white/60">Tell the vault how you'd like <span className="text-white">{card.title}</span> upgraded — sharper resolution, a redraw, fixed details, a new pose, etc. An admin will review and replace the image with a higher-quality one.</p>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Your prompt</Label>
                <Textarea
                  data-testid="enhance-prompt-input"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  maxLength={1000}
                  rows={4}
                  placeholder="e.g. Upscale to high-res and sharpen the face; keep the same outfit and pose."
                  className="mt-1 bg-[hsl(220_18%_12%)] border-white/10"
                />
                <div className="font-mono text-[10px] text-white/35 mt-1 text-right">{prompt.length}/1000</div>
              </div>
            </>
          )}

          <Button data-testid="enhance-submit-button" disabled={!ready || !affordable || submit.isPending} onClick={() => submit.mutate()} className="w-full bg-fuchsia-500 text-white hover:bg-fuchsia-400 disabled:opacity-50">
            {submit.isPending ? "Sending…" : free ? "Use Token & Send Request (free)" : !affordable ? "Not enough credits" : `Pay ${cost.toLocaleString()} & Send Request`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ListOnMarketDialog({ card, onClose, onListed }) {
  const [price, setPrice] = useState(card.base_value || 100);
  const [priceSp, setPriceSp] = useState(0);
  const feePct = card.market_fee_pct ?? 0.1;
  const p = Number(price) || 0;
  const sp = Number(priceSp) || 0;
  const fee = Math.round(p * feePct);
  const net = p - fee;
  const feeSp = Math.round(sp * feePct);
  const netSp = sp - feeSp;
  const canList = p > 0 || sp > 0;
  const list = useMutation({
    mutationFn: async () => (await api.post(`/market/list`, { card_id: card.id, price: p, price_sp: sp })).data,
    onSuccess: () => { toast.success("Listed on the Player Market"); onListed(); onClose(); },
    onError: (e) => toast.error(apiError(e, "Listing failed")),
  });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white tracking-[0.08em]">LIST ON MARKET</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <CardHoverPreview card={card} side="right">
              <div
                data-testid="listing-card-preview"
                className={`relative ${card.orientation === "landscape" ? "w-28 aspect-[3.5/2.5]" : "w-20 aspect-[2.5/3.5]"} rounded-lg overflow-hidden bg-black rarity-${card.rarity} card-aura cursor-help shrink-0`}
              >
                {card.front_image && <img src={card.front_image} alt={card.title} className="absolute inset-0 w-full h-full" style={cardImageStyle(card)} draggable={false} />}
                <div className="foil-overlay" />
              </div>
            </CardHoverPreview>
            <div className="text-sm text-white/60">List <span className="text-white">{card.title}</span> for other collectors to buy. Set a price in credits, in SP, or both — buyers pick whichever currency they have. While listed it&apos;s <span className="text-white">void-locked</span>.</div>
          </div>
          <div>
            <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Price (credits — leave 0 for SP-only)</Label>
            <Input data-testid="market-price-input" type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
          </div>
          <div>
            <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Price (SP — leave 0 for credits-only)</Label>
            <Input data-testid="market-price-sp-input" type="number" min={0} value={priceSp} onChange={(e) => setPriceSp(e.target.value)} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
          </div>
          <div className="hud-pill rounded-xl p-3 space-y-1 text-sm" data-testid="market-fee-breakdown">
            {p > 0 && <>
              <div className="flex items-center justify-between text-white/60"><span>Buyer pays</span><span className="font-mono text-white">{p.toLocaleString()}c</span></div>
              <div className="flex items-center justify-between text-white/60"><span>Store fee ({Math.round(feePct * 100)}%)</span><span className="font-mono text-red-300">-{fee.toLocaleString()}c</span></div>
              <div className="flex items-center justify-between border-t border-white/10 pt-1"><span className="text-white/80">You receive</span><span className="font-mono text-amber-300">{net.toLocaleString()}c</span></div>
            </>}
            {sp > 0 && <>
              {p > 0 && <div className="border-t border-white/10 my-1" />}
              <div className="flex items-center justify-between text-white/60"><span>Buyer pays</span><span className="font-mono text-white">{sp.toLocaleString()} SP</span></div>
              <div className="flex items-center justify-between text-white/60"><span>Store fee ({Math.round(feePct * 100)}%)</span><span className="font-mono text-red-300">-{feeSp.toLocaleString()} SP</span></div>
              <div className="flex items-center justify-between border-t border-white/10 pt-1"><span className="text-white/80">You receive</span><span className="font-mono text-fuchsia-300">{netSp.toLocaleString()} SP</span></div>
            </>}
            {!p && !sp && <span className="text-white/40 text-xs">Set a credit price, SP price, or both.</span>}
          </div>
          <Button data-testid="confirm-list-button" disabled={!canList || list.isPending} onClick={() => list.mutate()} className="w-full bg-cyan-400 text-black hover:bg-cyan-300">{list.isPending ? "Listing…" : "List for Sale"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TradeProposeDialog() { return null; }  // Deprecated — trades now flow through /requests.
function TradeForm() { return null; }
